import React from 'react';
import { useLocation } from 'react-router-dom';
import { enqueueEvent } from '../../services/eventMirror';

/**
 * Feeds `page_viewed` to the activity-log mirror on every React Router
 * navigation. Renders nothing.
 *
 * NOT routed through `captureEvent`, which would also hand it to PostHog —
 * where `initAnalytics` already sets `capture_pageview: 'history_change'`, so
 * PostHog counts the same navigation as `$pageview` on its own. Sending both
 * would double every page-view number in PostHog's funnels. `page_viewed`
 * exists in the union solely because the backend log has no equivalent of its
 * own, which is what the comment beside it in analytics.ts says.
 *
 * Carries `pathname` and NOT `location.search`, as defence in depth rather
 * than as a live fix. The password-reset flow puts a bearer-equivalent `token`
 * in the query string, and `properties` is stored verbatim in `user_events` —
 * a column the admin console renders. In the real app that token is already
 * gone by the time this renders: `captureAndScrubResetToken()` runs at module
 * load in index.tsx and `history.replaceState`s it out of the URL before React
 * mounts anything. (The test that demonstrates the leak uses `MemoryRouter`,
 * which never consults `window.location` and so bypasses that scrub — it pins
 * this layer, not a reachable bug.) Omitting `search` is still right: it costs
 * nothing, and it is the layer that does not depend on the scrub having run,
 * alongside PostHog's `sanitize_properties`.
 */
const PageViewMirror: React.FC = () => {
  const { pathname } = useLocation();
  // index.tsx wraps <App/> in <React.StrictMode>, which invokes every mount
  // effect twice in development. Without this ref the first navigation of every
  // dev session is recorded as two page views.
  const lastPath = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    enqueueEvent({ name: 'page_viewed', properties: { path: pathname } });
  }, [pathname]);

  return null;
};

export default PageViewMirror;
